import styled from '@emotion/styled';
import { first, partial } from 'lodash';
import { Button } from 'react-bootstrap';
import React, {
  useMemo,
  useEffect,
  useState,
  useRef,
  SetStateAction,
  Dispatch,
  MutableRefObject,
} from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import Pagination from 'react-js-pagination';

import { assetUrl } from '~/helpers';
import createMediaApi from '~/App/Api/Media';
import { MediaPagination } from './index';
import SimpleMediaItem from '../SimpleMediaItem';
import ListGroup from '~/App/Components/ListGroup';
import { Media as MediaProps, MediaType } from '~/types/api';
import { confirmAsync } from '~/App/Components/Modals/Confirm';
import { SpinnerNew as Spinner } from '~/App/Components/Shared/Spinner';
import { LoadingProgress } from '@vimily/bonjoro-ui-components-v2/LoadingProgress';

import Text from '~/App/Components/Text';
import { alertAsync } from '~/App/Components/Modals/Alert';
import { useUploadImageMedia } from '../../hooks/use-upload-image';

const StyledListGroup = styled(ListGroup)`
  margin-bottom: 10px;
  height: 550px;
`;

const PaginationContainer = styled.div`
  display: flex;
  margin: 0 0 15px;
  align-items: center;
  flex-direction: column;

  ul.pagination {
    margin: 0;
  }
`;

const FileInput = styled.input`
  &&& {
    display: none;
  }
`;
FileInput.defaultProps = {
  type: 'file',
};

const UploadButtons = styled(Button)`
  width: 130px;
  height: 40px;
  display: flex;
  justify-content: center;
  align-items: center;
  img {
    margin-right: 5px;
  }
`;

const EmptyState = styled.div`
  font-size: 20px;
  font-weight: 500;
  letter-spacing: -0.48px;
  color: #666666;
  text-align: center;
  padding-top: 120px;
  div {
    margin-top: 20px;
    font-size: 14px;
    line-height: 1.5;
    letter-spacing: 0.08px;
    text-align: center;
    color: #7d7c7c;
  }
`;

export interface UploadMediaTabProps {
  tags: string;
  perPage?: number;
  isUploadMediaSelected?: boolean;
  selectedId?: string | null;
  onItemDeleted: (media: MediaProps) => void;
}

export function UploadMediaButton({
  fileInput,
}: {
  fileInput: MutableRefObject<HTMLInputElement | undefined>;
}) {
  const handleUploadClick = () => {
    fileInput?.current?.click();
  };

  return (
    <UploadButtons bsStyle='info' onClick={handleUploadClick}>
      <img src={assetUrl('assets/images/icons/message-template/upload.svg')} />
      Upload
    </UploadButtons>
  );
}

export interface UploadMediaProps {
  tags: string;
  perPage?: number;
  selected: Partial<MediaProps>;
  setSelected: Dispatch<SetStateAction<Partial<MediaProps>>>;
  onItemDeleted: (media: MediaProps) => void;
  fileInput?: MutableRefObject<HTMLInputElement | undefined>;
}

interface UploadMediaError {
  response: {
    data: {
      message: string;
    };
  };
}

function renderUploadMediaErrorAlertContent(message: string) {
  return (
    <>
      <Text.SubHeading>Error Uploading Image</Text.SubHeading>
      <Text.BodyCopy>{message}</Text.BodyCopy>
    </>
  );
}

export default function UploadMediaTab({
  tags,
  perPage = 7,
  selected,
  setSelected,
  onItemDeleted,
  fileInput: fileInputRef,
}: UploadMediaProps) {
  const [pagination, setPagination] = useState<MediaPagination>({
    activePage: 1,
  });
  const isUploadedMediaSelected = selected?.type === MediaType.Uploaded;

  const fileInput = fileInputRef || useRef<HTMLInputElement>();
  const mediaApi = createMediaApi();
  const queryClient = useQueryClient();
  const [uploadProgress, setUploadProgress] = useState(0);

  const mediaParams = useMemo(() => {
    return {
      page: pagination?.activePage,
      tags,
      limit: perPage,
    };
  }, [pagination.activePage]);

  const { isLoading: isMediaLoading, data: media } = useQuery(
    ['media-query', mediaParams],
    () => mediaApi.getAll(mediaParams),
    { keepPreviousData: true }
  );

  async function deleteMediaAction({ id }: Pick<MediaProps, 'id'>) {
    await mediaApi.delete(id);
  }

  const { mutateAsync: deleteMedia } = useMutation(deleteMediaAction, {
    onSuccess: () => queryClient.invalidateQueries('media-query'),
  });

  const { mutateAsync: uploadMedia } = useMutation(useUploadImageMedia, {
    onSuccess: () => {
      if (fileInput.current) {
        fileInput.current.value = '';
      }
      setPagination({ ...pagination, activePage: 1 });
      queryClient.invalidateQueries('media-query');
      setUploadProgress(0);
    },
    onError: async (error: UploadMediaError) => {
      const alertContent = renderUploadMediaErrorAlertContent(
        error.response.data.message
      );
      return await alertAsync(alertContent);
    },
  });

  useEffect(() => {
    if (media) {
      setPagination({
        activePage: media.current_page,
        itemsCountPerPage: media.per_page,
        totalItemsCount: media.total,
        lastPage: media.last_page,
        count: media.data.length,
      });
    }
  }, [media]);

  const displayPagination = useMemo(
    () => (pagination?.lastPage ?? 0) > 1,
    [pagination]
  );

  const handleDeleteClick = async (media) => {
    if (
      await confirmAsync(
        'Are you sure you want to delete this media file? Deletion is permanent!'
      )
    ) {
      await deleteMedia(media);

      const page =
        pagination?.activePage == pagination?.lastPage &&
        (pagination?.count ?? 0) > 1
          ? pagination?.activePage
          : (pagination?.lastPage ?? 1) - 1;

      setPagination({
        ...pagination,
        activePage: page,
        totalItemsCount: media.total,
        lastPage: media.last_page,
      });

      if (typeof onItemDeleted === 'function') {
        onItemDeleted(media);
      }
    }
  };

  const handleFileChange = () => {
    const file = first<string | Blob>(fileInput?.current?.files);

    if (file == null) {
      return;
    }

    const imageMaxFileSizeInMb = 2048;
    const fileSizeInMb = file.size / 1024;

    if (fileSizeInMb > imageMaxFileSizeInMb) {
      if (fileInput.current) {
        fileInput.current.value = '';
      }
      const alertContent = renderUploadMediaErrorAlertContent(
        'Please upload an image no larger than 2MB in size.'
      );
      return alertAsync(alertContent);
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('tags', tags);

    uploadMedia({ formData, onUploadProgress: setUploadProgress });
  };

  if (isMediaLoading) {
    return <Spinner style={{ marginTop: '20%' }} />;
  }

  return (
    <>
      <StyledListGroup>
        {fileInput.current?.value && (
          <LoadingProgress
            progress={uploadProgress}
            className='h-[20px] mb-4'
          />
        )}
        {media?.data.map(function MapMedia(media: MediaProps) {
          return (
            <SimpleMediaItem
              key={media.id}
              media={media}
              active={isUploadedMediaSelected && selected.id == media.id}
              onClick={() => setSelected(media)}
              onDeleteClick={partial(handleDeleteClick, media)}
            />
          );
        })}

        {!media?.data.length && (
          <EmptyState>
            No images uploaded
            <div>
              File types can be .jpg or .png
              <br />
              Images present best at 2250 x 1500 px
            </div>
          </EmptyState>
        )}
      </StyledListGroup>
      {displayPagination && (
        <PaginationContainer>
          <Pagination
            {...pagination}
            onChange={(page) =>
              setPagination({ ...pagination, activePage: page })
            }
            pageRangeDisplayed={5}
          />
        </PaginationContainer>
      )}
      <FileInput
        id={'upload-media-input'}
        ref={fileInput}
        onChange={handleFileChange}
        accept='image/*'
      />
    </>
  );
}
